import { request } from "./client";
import type {
  TimeEventInput,
  TimeEventResponse,
  ActivityCatalogEntry,
  ActivityCatalogResponse,
  SessionNote,
  ReimputeRef,
  Reimputation,
  ReimputationBatch,
} from "./types";

/** POST /api/v1/tawa/activities/start — body must include activity_code; event_type = 'activity_start'. */
export async function startActivity(
  body: TimeEventInput,
): Promise<TimeEventResponse> {
  return request<TimeEventResponse>({
    method: "POST",
    url: "/activities/start",
    data: body,
  });
}

/** POST /api/v1/tawa/activities/end — body event_type = 'activity_end'. activity_code optional but recommended. */
export async function endActivity(
  body: TimeEventInput,
): Promise<TimeEventResponse> {
  return request<TimeEventResponse>({
    method: "POST",
    url: "/activities/end",
    data: body,
  });
}

export async function listActivities(): Promise<ActivityCatalogResponse> {
  return request<ActivityCatalogResponse>({
    method: "GET",
    url: "/activity-types",
  });
}

export async function adminListActivities(): Promise<ActivityCatalogResponse> {
  return request<ActivityCatalogResponse>({
    method: "GET",
    url: "/activity-types",
    params: { include_inactive: true },
  });
}

export async function createActivity(
  entry: Omit<ActivityCatalogEntry, "id">,
): Promise<ActivityCatalogEntry> {
  return request<ActivityCatalogEntry>({
    method: "POST",
    url: "/activity-types",
    data: entry,
  });
}

export async function updateActivity(
  id: string,
  entry: Partial<ActivityCatalogEntry>,
): Promise<ActivityCatalogEntry> {
  return request<ActivityCatalogEntry>({
    method: "PATCH",
    url: `/activity-types/${id}`,
    data: entry,
  });
}

export async function deactivateActivity(
  id: string,
): Promise<ActivityCatalogEntry> {
  return request<ActivityCatalogEntry>({
    method: "POST",
    url: `/activity-types/${id}/deactivate`,
  });
}

/** PATCH /api/v1/tawa/activities/{startEventId}/note — worker self-service edit.
 * Body : { note } seul ; les liens (context_refs) ne s'éditent plus après capture. */
export async function updateActivityNote(
  startEventId: string,
  note: string,
): Promise<void> {
  await request<unknown>({
    method: "PATCH",
    url: `/activities/${encodeURIComponent(startEventId)}/note`,
    data: { note },
  });
}

export async function getSessionNote(): Promise<SessionNote | null> {
  try {
    return await request<SessionNote>({ method: "GET", url: "/session/note" });
  } catch {
    return null;
  }
}

export async function updateSessionNote(note: string): Promise<SessionNote> {
  return request<SessionNote>({
    method: "PATCH",
    url: "/session/note",
    data: { note },
  });
}

export interface ReimputeEnvelope {
  reason: string;
  correlation_id: string;
}

/** POST /api/v1/tawa/activities/{startEventId}/reimpute : nouvelle référence
 * projet, l'original reste intact. `correlation_id` = clé d'idempotence. */
export async function reimputeActivity(
  startEventId: string,
  body: ReimputeRef & ReimputeEnvelope,
): Promise<Reimputation> {
  return request<Reimputation>({
    method: "POST",
    url: `/activities/${encodeURIComponent(startEventId)}/reimpute`,
    data: body,
  });
}

/** POST /api/v1/tawa/activities/reimpute : 1 à 200 activités, tout ou rien. */
export async function reimputeActivities(
  body: ReimputeEnvelope & {
    items: (ReimputeRef & { event_id: string })[];
  },
): Promise<ReimputationBatch> {
  return request<ReimputationBatch>({
    method: "POST",
    url: "/activities/reimpute",
    data: body,
  });
}
